import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setOpenCheckout } from '../../redux/slices/cartSlice';
import seatInfoFormatter from '../../utils/formatters';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';

function DesktopDrawer({ drawerWidth, seats, allSeats, totalPrice, onRemove, onRemoveAll, onCancel }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const theme = useTheme();

  const onCheckout = () => {
    onCancel();
    dispatch(setOpenCheckout(true));
  };

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="permanent"
      anchor="right"
    >
      {seats.length ? (
        <List sx={{ overflow: 'auto' }}>
          {seats.map(
            (
              {
                id,
                singleType,
                typeId,
                typeLabel,
                isDefaultType,
                section,
                row,
                seat,
                basePrice,
                price,
                discount,
                isFree,
                categoryLabel,
                categoryColor,
              },
              index
            ) => (
              <ListItem sx={{ borderLeft: `5px solid ${categoryColor}` }} key={`${id}-${index}`} divider>
                <ListItemText
                  sx={{ maxWidth: 200 }}
                  primary={
                    <>
                      {categoryLabel}
                      {!isDefaultType && <Chip sx={{ maxWidth: 100, marginLeft: 1 }} size="small" label={typeLabel} />}
                    </>
                  }
                  secondary={seatInfoFormatter(section, row, seat, t)}
                />
                <ListItemSecondaryAction>
                  <Grid container justifyItems="center" alignItems="center">
                    {isFree || discount ? (
                      <Stack>
                        <span style={{ textDecoration: 'line-through', fontSize: '0.70rem' }}>
                          {(isFree ? price : basePrice).toFixed(2) + ' Lei'}
                        </span>
                        <span style={{ color: theme.palette.error.main }}>
                          {(isFree ? 0 : price).toFixed(2) + ' Lei'}
                        </span>
                      </Stack>
                    ) : (
                      price.toFixed(2) + ' Lei'
                    )}
                    <IconButton edge="end" aria-label="delete" onClick={() => onRemove(id, singleType ? null : typeId)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </ListItemSecondaryAction>
              </ListItem>
            )
          )}
          {!!seats.length && (
            <ListItem>
              <Button fullWidth variant="text" size="small" onClick={onRemoveAll}>
                {t('Șterge')}
              </Button>
            </ListItem>
          )}
        </List>
      ) : (
        <Box sx={{ height: '100%' }} display="flex" alignItems="center" justifyContent="center">
          <Grid item>
            <Typography variant="h6">{t('Niciun loc selectat')}</Typography>
          </Grid>
        </Box>
      )}
      <List
        sx={{
          mt: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          p: 1,
        }}
      >
        <Divider sx={{ mb: 1 }} />

        <ListItem
          sx={(theme) => ({
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            mb: 1,
          })}
        >
          <ListItemText
            primary="Total"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: '1rem',
            }}
          />
          <ListItemSecondaryAction>
            <Typography variant="subtitle1" fontWeight={700}>
              {`${totalPrice.toFixed(2)} Lei`}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>

        <ListItem dense sx={{ py: 0.5 }}>
          <ListItemText primary={t('Nr. / Nr. Total')} />
          <ListItemSecondaryAction>
            <Typography variant="body2">
              {seats.length}
              {t('/')}
              {allSeats.length}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>

        <Divider sx={{ my: 1 }} />

        <ListItem>
          <Button onClick={onCheckout} fullWidth variant="contained" size="large">
            {t('Checkout')}
          </Button>
        </ListItem>

        <ListItem>
          <Button onClick={onCancel} fullWidth variant="outlined" size="large">
            {t('Close')}
          </Button>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default DesktopDrawer;
